import authService from './authService';
import { getDataApiUrl, API_CONFIG } from '../config/api';

export interface CreateSessionRequest {
  carId: number;
  carName: string;
  imei: string;
}

export interface UpdateSessionRequest {
  endTime: string;
  initialFuelLevel: number;
  finalFuelLevel: number;
  fuelConsumption: number;
  fuelRefill: number;
  netChange: number;
  totalSensors: number;
  dataPointsCount: number;
  status: 'COMPLETED' | 'CANCELLED';
}

export interface TrackingSessionResponse {
  id: number;
  carId: number;
  carName: string;
  imei: string;
  startTime: string;
  endTime: string | null;
  durationSeconds: number | null;
  formattedDuration: string;
  initialFuelLevel: number | null;
  finalFuelLevel: number | null;
  fuelConsumption: number | null;
  fuelRefill: number | null;
  netChange: number | null;
  totalSensors: number | null;
  dataPointsCount: number | null;
  status: 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
  createdAt: string;
  updatedAt: string;
}

class FuelTrackingService {
  private baseUrl = '/api/fuel-tracking';

  private getHeaders(): HeadersInit {
    const token = authService.getAccessToken();
    console.log('FuelTrackingService - token from authService:', token);
    console.log('FuelTrackingService - token exists:', !!token);
    console.log('FuelTrackingService - token length:', token ? token.length : 0);
    
    // Проверяем также localStorage напрямую
    const directToken = localStorage.getItem('accessToken');
    console.log('FuelTrackingService - direct token from localStorage:', directToken);
    
    // Если токен отсутствует, возвращаем заголовки без авторизации
    if (!token) {
      console.warn('FuelTrackingService - No access token found, proceeding without authorization');
      return {
        ...API_CONFIG.HEADERS
      };
    }
    
    const headers = {
      ...API_CONFIG.HEADERS,
      'Authorization': `Bearer ${token}`
    };
    
    console.log('FuelTrackingService - headers:', headers);
    return headers;
  }

  async createSession(data: CreateSessionRequest): Promise<TrackingSessionResponse> {
    const url = getDataApiUrl(`${this.baseUrl}/sessions`);
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  async completeSession(sessionId: number, data: UpdateSessionRequest): Promise<TrackingSessionResponse> {
    const url = getDataApiUrl(`${this.baseUrl}/sessions/${sessionId}/complete`);
    const response = await fetch(url, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  async getActiveSession(carId: number): Promise<TrackingSessionResponse | null> {
    const url = getDataApiUrl(`${this.baseUrl}/cars/${carId}/active-session`);
    const response = await fetch(url, {
      headers: this.getHeaders()
    });
    
    if (response.status === 404) {
      return null;
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  async getSessionsByCar(carId: number): Promise<TrackingSessionResponse[]> {
    console.log('FuelTrackingService - getSessionsByCar called for carId:', carId);
    const url = getDataApiUrl(`${this.baseUrl}/cars/${carId}/sessions`);
    console.log('FuelTrackingService - URL:', url);
    console.log('FuelTrackingService - Headers:', this.getHeaders());
    
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders()
    });
    
    console.log('FuelTrackingService - Response status:', response.status);
    console.log('FuelTrackingService - Response ok:', response.ok);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('FuelTrackingService - Error response:', errorData);
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('FuelTrackingService - Response data:', data);
    console.log('FuelTrackingService - Data type:', typeof data);
    console.log('FuelTrackingService - Is array:', Array.isArray(data));
    
    return data;
  }

  async getAllSessions(): Promise<TrackingSessionResponse[]> {
    console.log('FuelTrackingService - getAllSessions called');
    const url = getDataApiUrl(`${this.baseUrl}/sessions`);
    console.log('FuelTrackingService - URL:', url);
    console.log('FuelTrackingService - Headers:', this.getHeaders());
    
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders()
    });
    
    console.log('FuelTrackingService - Response status:', response.status);
    console.log('FuelTrackingService - Response ok:', response.ok);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('FuelTrackingService - Error response:', errorData);
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('FuelTrackingService - Response data:', data);
    console.log('FuelTrackingService - Data type:', typeof data);
    console.log('FuelTrackingService - Is array:', Array.isArray(data));
    
    return data;
  }

  async getSessionById(sessionId: number): Promise<TrackingSessionResponse> {
    const url = getDataApiUrl(`${this.baseUrl}/sessions/${sessionId}`);
    const response = await fetch(url, {
      headers: this.getHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }
}

export default new FuelTrackingService(); 